import Vue from "vue";
import Vuex from "vuex";
import createPersistedState from "vuex-persistedstate";

import leiloes from "@/store/modules/leiloes.js";
import agendamento from "./modules/agendamento";
import cadastro from "./modules/cadastro";
import categoria from "./modules/categoria";
import lance from "./modules/lance";
import login from "./modules/login";
import lote from "./modules/lote";
import paginas from "./modules/paginas";

Vue.use(Vuex);

const PERSIST_KEY = "vuex";

// TTL (Time To Live) por módulo persistido, em milissegundos
// Ajuste conforme necessidade de negócio.
const PERSIST_TTL_MS = {
	// Ex.: sessão de login expira em 8 horas
	login: 8 * 60 * 60 * 1000,
	// Ex.: dados de categoria/páginas podem expirar em 24 horas
	categoria: 24 * 60 * 60 * 1000,
	paginas: 24 * 60 * 60 * 1000,
};

/**
 * Serializa estado para armazenamento com metadados de expiração por chave.
 * @param {string} key - Chave base do storage
 * @param {Record<string, any>} state - Subconjunto de estado (paths) a persistir
 * @param {Storage} storage - Implementação de storage (localStorage por padrão)
 * @returns {void}
 */
function setStateWithExpiry(key, state, storage) {
	const now = Date.now();
	const expires = {};
	Object.keys(state).forEach((k) => {
		const ttl = PERSIST_TTL_MS[k];
		if (typeof ttl === "number" && ttl > 0) {
			expires[k] = now + ttl;
		}
	});
	const payload = { __persist: { data: state, expires } };
	try {
		storage.setItem(key, JSON.stringify(payload));
	} catch (_) {
		// Evitar falha dura em ambientes sem storage
		void _; // no-op para satisfazer eslint no-empty
	}
}

/**
 * Lê e higieniza estado persistido, removendo chaves expiradas.
 * Compatível com dados legados sem metadados (retorna objeto cru).
 * @param {string} key - Chave base do storage
 * @param {Storage} storage - Implementação de storage (localStorage por padrão)
 * @returns {Record<string, any>} - Estado reidratado filtrado
 */
function getStateWithExpiry(key, storage) {
	let raw;
	try {
		raw = storage.getItem(key);
	} catch (_) {
		return {};
	}
	if (!raw) return {};
	try {
		const parsed = JSON.parse(raw);
		// Dados legados: retornar diretamente
		if (!parsed || !parsed.__persist) return parsed || {};

		const now = Date.now();
		const data = parsed.__persist.data || {};
		const expires = parsed.__persist.expires || {};
		let changed = false;

		Object.keys(expires).forEach((k) => {
			const expAt = expires[k];
			if (typeof expAt === "number" && now >= expAt) {
				delete data[k];
				delete expires[k];
				changed = true;
			}
		});

		if (changed) {
			// Persistir estado já limpo para manter storage consistente
			try {
				storage.setItem(key, JSON.stringify({ __persist: { data, expires } }));
			} catch (_) {
				// Silencioso por segurança
				void _; // no-op para satisfazer eslint no-empty
			}
		}

		return data;
	} catch (_) {
		// Em caso de dados corruptos, limpar entrada e retornar objeto vazio
		try {
			storage.removeItem(key);
		} catch (_) { void _; }
		return {};
	}
}

const dataState = createPersistedState({
	key: PERSIST_KEY,
	paths: ["login", "categoria", "paginas"],
	// Customiza serialização para incluir expiração
	setState: setStateWithExpiry,
	// Customiza leitura para remover chaves expiradas
	getState: getStateWithExpiry,
});

const store = new Vuex.Store({
	plugins: [dataState],
	modules: {
		leiloes,
		lote,
		login,
		cadastro,
		categoria,
		lance,
		agendamento,
		paginas,
	},
});

/**
 * Força a limpeza do cache persistido do Vuex.
 * Remove a entrada do storage associada à chave de persistência.
 * @param {Storage} [storage=window.localStorage] - Storage alvo
 */
export const clearPersistedState = (storage = window.localStorage) => {
	try {
		storage.removeItem(PERSIST_KEY);
	} catch (_) {
		// Silencioso por segurança
		void _; // no-op para satisfazer eslint no-empty
	}
};

export default store;
